import { FC, useState, useCallback, useEffect } from 'react'

import BaseDialog from '~/src/components/shared/BaseDialog'

import useTrainingRequest from '~/src/components/TrainingRequest/hook'
import useTrackCvtEvent from '~/src/hooks/useTrackCvtEvent'
import useTranslation from '~/src/hooks/useTranslation'

import { MixpanelEvent } from '~/clients/mixpanelClient'

type Props = {
  isOpen: boolean
  onCloseHandler: () => void
}

const TrainingRequest: FC<Props> = ({ isOpen, onCloseHandler }) => {
  const [details, setDetails] = useState('')

  const { t } = useTranslation('common')
  const trackCvtEvent = useTrackCvtEvent()

  const [submitTrainingRequest, { isLoading, status }] = useTrainingRequest()

  useEffect(() => {
    if (status === 'success') {
      onCloseHandler()
    }
  }, [status, onCloseHandler])

  const onSubmit = useCallback(() => {
    submitTrainingRequest(details, () =>
      trackCvtEvent(MixpanelEvent.CVTTrainingRequestSubmitted, {
        topicEntered: details.trim() !== '',
      })
    )
  }, [details, submitTrainingRequest, trackCvtEvent])

  const onCloseDialog = useCallback(() => {
    if (!isLoading) {
      trackCvtEvent(MixpanelEvent.CVTTrainingRequestCancelled)
    }
    onCloseHandler()
  }, [isLoading, trackCvtEvent, onCloseHandler])

  return (
    <BaseDialog
      className="w-[25rem]"
      isOpen={isOpen}
      loading={isLoading}
      primaryButtonText={t('buttons.submit')}
      secondaryButtonText={t('buttons.cancel')}
      title={t('trainingRequest.title')}
      onClose={onCloseDialog}
      onConfirm={onSubmit}
    >
      <div className="space-y-4 text-body-m">
        <p className="text-secondary">{t('trainingRequest.description')}</p>

        <div className="flex flex-col space-y-1">
          <label className="text-body-m-emphasized" htmlFor="training-request-details">
            {t('trainingRequest.detailsLabel')}
          </label>
          <textarea
            className="h-40 w-full resize-none appearance-none rounded-md border border-input p-2 text-body-m focus:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-600"
            id="training-request-details"
            placeholder={t('trainingRequest.detailsPlaceholder')}
            onChange={(event) => setDetails(event.target.value)}
          />
          <p className="text-body-s text-secondary">
            {t('trainingRequest.detailsHelper')}
          </p>
        </div>

        {status === 'error' && (
          <div className="text-critical">{t('reportIssue.somethingWentWrong')}</div>
        )}
      </div>
    </BaseDialog>
  )
}

export default TrainingRequest
